/**
 * Cart Shipping Threshold Calculator
 * Ensures the free shipping threshold is properly converted for different currencies
 * Works with the existing cart drawer components
 */
class ShippingThresholdCalculator {
  constructor() {
    // Base threshold in GBP
    this.baseThresholdGBP = 50;
    
    // Currency conversion rates (update these with current rates)
    this.currencyRates = {
      'GBP': 1,       // Base currency
      'USD': 1.30,    // 1 GBP = 1.30 USD
      'AUD': 2.14,    // 1 GBP = 2.14 AUD
      'EUR': 1.16,    // 1 GBP = 1.16 EUR
      'CAD': 1.78,    // 1 GBP = 1.78 CAD
      'ZAR': 20.0     // 1 GBP = 20.0 ZAR
    };
    
    // Initialize
    this.init();
  }
  
  init() {
    // Get current currency
    this.currentCurrency = Shopify && Shopify.currency ? Shopify.currency.active : 'GBP';
    
    // Calculate threshold in current currency
    this.calculateThreshold();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Initial update
    this.updateUI();
  }
  
  calculateThreshold() {
    // Default to GBP threshold if currency not found
    this.currencyThreshold = this.baseThresholdGBP;
    
    // Convert to current currency if rate exists
    if (this.currencyRates[this.currentCurrency]) {
      this.currencyThreshold = Math.round(this.baseThresholdGBP * this.currencyRates[this.currentCurrency]);
    }
    
    return this.currencyThreshold;
  }
  
  setupEventListeners() {
    // Listen for cart updates
    document.addEventListener('cart:updated', this.updateUI.bind(this));
    
    // Update when cart drawer opens
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const originalOpen = cartDrawer.open;
      const self = this;
      
      cartDrawer.open = function(...args) {
        originalOpen.apply(this, args);
        self.updateUI();
      };
    }
    
    // Also update on DOMContentLoaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.updateUI.bind(this));
    } else {
      this.updateUI();
    }
  }
  
  updateUI() {
    // Get cart total
    const totalElement = document.querySelector('.totals__total-value');
    if (!totalElement) return;
    
    const cartTotal = parseFloat(totalElement.getAttribute('data').replace(',', '.'));
    if (isNaN(cartTotal)) return;
    
    // Update threshold in data attribute for liquid template
    const progressElements = document.querySelectorAll('[data-limit]');
    progressElements.forEach(el => {
      el.setAttribute('data-limit', this.currencyThreshold);
    });
    
    // Calculate remaining amount and progress percentage
    const remaining = Math.max(0, this.currencyThreshold - cartTotal);
    let percent = (cartTotal / this.currencyThreshold) * 100;
    percent = Math.min(100, Math.max(0, percent)); // Clamp between 0-100
    
    // Update progress bar
    const progressBar = document.querySelector('.progress_shipping .progress-meter');
    if (progressBar) {
      progressBar.style.width = `${percent}%`;
    }
    
    // Update progress amount text
    const progressAmount = document.querySelector('.progress_amount');
    if (progressAmount) {
      progressAmount.textContent = remaining.toFixed(2);
    }
    
    // Show free shipping message or remaining amount message
    const progressRemain = document.querySelector('.progress_remain');
    const freeShipping = document.querySelector('.free_shipping');
    
    if (progressRemain && freeShipping) {
      if (percent >= 100) {
        progressRemain.style.display = 'none';
        freeShipping.style.display = 'block';
      } else {
        progressRemain.style.display = 'block';
        freeShipping.style.display = 'none';
      }
    }
    
    // Update currency symbol
    const currencySymbol = document.querySelector('.currency_symbol');
    if (currencySymbol) {
      currencySymbol.textContent = this.getCurrencySymbol();
    }
  }
  
  getCurrencySymbol() {
    const symbols = {
      'GBP': '£',
      'USD': '$',
      'AUD': 'A$',
      'EUR': '€',
      'CAD': 'C$',
      'ZAR': 'R'
    };
    
    return symbols[this.currentCurrency] || '£';
  }
}

// Initialize the shipping threshold calculator after DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.shippingThresholdCalculator = new ShippingThresholdCalculator();
});

// Also initialize immediately if DOM is already loaded
if (document.readyState !== 'loading') {
  window.shippingThresholdCalculator = new ShippingThresholdCalculator();
}

// Hook into the Cart Drawer class to update shipping threshold on cart changes
class CartDrawerExtension {
  static initialize() {
    const originalRenderContents = CartDrawer.prototype.renderContents;
    
    CartDrawer.prototype.renderContents = function(...args) {
      originalRenderContents.apply(this, args);
      
      // Update shipping threshold after cart contents are rendered
      if (window.shippingThresholdCalculator) {
        setTimeout(() => {
          window.shippingThresholdCalculator.updateUI();
        }, 100);
      }
    };
  }
}

// Initialize extension after CartDrawer is defined
if (typeof CartDrawer !== 'undefined') {
  CartDrawerExtension.initialize();
} else {
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof CartDrawer !== 'undefined') {
      CartDrawerExtension.initialize();
    }
  });
}